import { useState } from 'react';
import LangEnum from '../../../../../lang/LangEnum';
import { CatalogClassificationType } from '../components/ClassificationTypeSelect/interfaces/classificationTypeSelect.types';
import {
  ExportFormValues,
  ExportImageModel,
  ExportMethod,
  ExportQuality,
  ExportType,
  UseCatalogExportModalParams,
} from '../types/catalogExportModal.types';

const useCatalogExportModal = (params: UseCatalogExportModalParams) => {
  const [values, setValues] = useState<ExportFormValues>({
    exportType: ExportType.PDF,
    exportMethod: ExportMethod.EMAIL,
    exportQuality: ExportQuality.HIGH,
    exportImageModel: ExportImageModel.MEDIUM,
    classificationTypeId: params.initialClassificationTypeId ?? undefined,
  });
  const [prevIsModalOpen, setPrevIsModalOpen] = useState(params.isModalOpen);
  const [errorHelperTextPriceTable, setErrorHelperTextPriceTable] = useState('');

  if (prevIsModalOpen !== params.isModalOpen) {
    setPrevIsModalOpen(params.isModalOpen);
  }

  if (!prevIsModalOpen && params.isModalOpen) {
    setValues((prev) => ({
      ...prev,
      classificationTypeId: params.initialClassificationTypeId ?? undefined,
    }));
  }

  const isExportTypeXlsSelected = () => values.exportType === ExportType.XLSX;

  const getHelperText = (receivedValues: ExportFormValues) => {
    const isHighQuality = receivedValues.exportQuality === ExportQuality.HIGH;
    const isPdf = receivedValues.exportType === ExportType.PDF;
    if (isHighQuality && isPdf) {
      return params.translate(LangEnum.CATALOGS_EXPORT_HELPER_TEXT_HIGH_QUALITY);
    }

    if (receivedValues.exportMethod === ExportMethod.DOWNLOAD) {
      return params.translate(LangEnum.CATALOGS_EXPORT_HELPER_TEXT_METHOD_DOWNLOAD);
    }

    return '';
  };

  const handleValueExportViaChange = (value: string) => {
    setValues({ ...values, exportMethod: value as ExportFormValues['exportMethod'] });
  };

  const handleValuePriceTableIdChange = (value: string | null) => {
    if (!value) {
      setErrorHelperTextPriceTable(params.translate(LangEnum.GLOBAL_REQUIRED));
    } else {
      setErrorHelperTextPriceTable('');
    }
    setValues({ ...values, priceTableId: value as ExportFormValues['priceTableId'] });
  };

  const handleValueClassificationIdChange = (
    classificationType: CatalogClassificationType | null,
  ) => {
    const value = classificationType?.id ?? undefined;
    setValues((prev) => ({
      ...prev,
      classificationTypeId: value as ExportFormValues['classificationTypeId'],
    }));
  };

  const handleValueTypeChange = (value: string) => {
    const newValue = value as ExportFormValues['exportType'];
    const updatedValues = { ...values, exportType: newValue };

    if (value === ExportType.XLSX) {
      updatedValues.exportMethod = ExportMethod.DOWNLOAD;
    }

    if (value === ExportType.PDF && values.exportQuality === ExportQuality.HIGH) {
      updatedValues.exportMethod = ExportMethod.EMAIL;
    }
    setValues(updatedValues);
  };

  const handleValueQualityChange = (value: string) => {
    if (value === ExportQuality.HIGH) {
      setValues({
        exportType: ExportType.PDF,
        exportMethod: ExportMethod.EMAIL,
        exportQuality: value as ExportFormValues['exportQuality'],
        exportImageModel: values.exportImageModel,
        priceTableId: values.priceTableId,
        classificationTypeId: values.classificationTypeId,
      });

      return;
    }

    setValues({ ...values, exportQuality: value as ExportFormValues['exportQuality'] });
  };

  const handleSubmit = () => {
    const errorMessage =
      values.exportType === ExportType.PDF
        ? params.translate(LangEnum.CATALOGS_EXPORT_PDF_ERROR)
        : params.translate(LangEnum.CATALOGS_EXPORT_XLS_ERROR);

    if (!isExportTypeXlsSelected() && !values.priceTableId) {
      setErrorHelperTextPriceTable(params.translate(LangEnum.GLOBAL_REQUIRED));
      return;
    }

    params.onSubmit({
      values,
      isAdminSeller: params.isAdminSeller,
      messages: {
        onSuccessMessage: params.translate(LangEnum.CATALOGS_EXPORT_SUCCESS_EMAIL),
        onErrorMessage: errorMessage,
      },
    });
  };

  const handleChangeImageModel = (value: ExportImageModel) => {
    setValues({ ...values, exportImageModel: value });
  };

  return {
    values,
    getHelperText,
    isExportTypeXlsSelected,
    errorHelperTextPriceTable,
    handleSubmit,
    handleValueTypeChange,
    handleChangeImageModel,
    handleValueQualityChange,
    handleValueExportViaChange,
    handleValuePriceTableIdChange,
    handleValueClassificationIdChange,
  };
};

export default useCatalogExportModal;
